import { useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Pencil,
  Trash2,
  Hash,
  Sparkles,
  Info,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  RotateCcw,
  Search,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label,
  Switch,
} from '@jira-clone/ui';
import { Spinner } from '@/shared/components/ui/Spinner';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useEmployeeCodeSeries,
  useEmployeeCodeSeriesMutations,
  type EmployeeCodeSeries,
} from '../hooks/useEmployeeCodeSeries';

interface RuleFormState {
  id?: string;
  department: string;
  roleKeywordsText: string;
  isAnyRole: boolean;
  code: string;
  label: string;
  isActive: boolean;
}

const EMPTY_FORM: RuleFormState = {
  department: '',
  roleKeywordsText: '',
  isAnyRole: true,
  code: '',
  label: '',
  isActive: true,
};

export function HrEmployeeCodesPage() {
  const { data: seriesList = [], isLoading, isError, refetch } = useEmployeeCodeSeries();
  const { createSeries, updateSeries, deleteSeries } = useEmployeeCodeSeriesMutations();

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [searchTerm, setSearchTerm] = useState('');

  const handleOpenAdd = () => {
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const handleOpenEdit = (item: EmployeeCodeSeries) => {
    const isAny = !item.roleKeywords || item.roleKeywords.length === 0 || item.roleKeywords.includes('any');
    setForm({
      id: item.id,
      department: item.department,
      roleKeywordsText: isAny ? '' : item.roleKeywords.join(', '),
      isAnyRole: isAny,
      code: item.code,
      label: item.label || '',
      isActive: item.isActive,
    });
    setModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanDept = form.department.trim().toLowerCase();
    const cleanCode = form.code.trim().toUpperCase();

    if (!cleanDept) {
      toast.error('Please specify a department name.');
      return;
    }

    if (!/^[A-Z]{2}$/.test(cleanCode)) {
      toast.error('Series code must be exactly two letters (e.g. FD, HR, BD).');
      return;
    }

    const keywords = form.isAnyRole
      ? ['any']
      : form.roleKeywordsText
          .split(',')
          .map((k) => k.trim().toLowerCase())
          .filter(Boolean);

    if (!form.isAnyRole && keywords.length === 0) {
      toast.error('Please enter at least one role keyword (e.g. "graphic designer", "video", "bde").');
      return;
    }

    try {
      if (form.id) {
        await updateSeries.mutateAsync({
          id: form.id,
          payload: {
            department: cleanDept,
            code: cleanCode,
            roleKeywords: keywords,
            label: form.label.trim(),
            isActive: form.isActive,
          },
        });
        toast.success(`Updated series ${cleanCode}`);
      } else {
        await createSeries.mutateAsync({
          department: cleanDept,
          code: cleanCode,
          roleKeywords: keywords,
          label: form.label.trim(),
          isActive: form.isActive,
        });
        toast.success(`Created series ${cleanCode}`);
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save series rule.'));
    }
  };

  const handleDelete = async (item: EmployeeCodeSeries) => {
    if ((item.inUse ?? 0) > 0) {
      toast.error(`Cannot delete series "${item.code}" because it is currently in use by ${item.inUse} record(s).`);
      return;
    }
    if (!window.confirm(`Delete rule for department "${item.department}" (Code: ${item.code})?`)) return;

    try {
      await deleteSeries.mutateAsync(item.id);
      toast.success('Series rule deleted.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete series rule.'));
    }
  };

  const filtered = seriesList.filter((s) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      s.department.toLowerCase().includes(q) ||
      s.code.toLowerCase().includes(q) ||
      s.label.toLowerCase().includes(q) ||
      s.roleKeywords.some((k) => k.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-6">
      {/* Top Banner & Stat Cards */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-2 border-b border-border">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Employee Code Series</h1>
            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 text-xs font-semibold">
              Master Data
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Department and role-based employee code prefix rules. Auto-issued when team members join or switch departments in the format{' '}
            <span className="font-mono font-medium text-foreground">DT-26-[CODE][001]</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5 text-xs">
            <RotateCcw className="h-3.5 w-3.5" />
            Refresh
          </Button>
          <Button size="sm" onClick={handleOpenAdd} className="gap-1.5 text-xs">
            <Plus className="h-4 w-4" />
            Add Series Rule
          </Button>
        </div>
      </div>

      {/* Info Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
        <Card className="p-4 bg-card/60 backdrop-blur-sm border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Standard Prefix</span>
            <Hash className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-xl font-mono font-bold text-foreground">DT-26</span>
            <span className="text-[10px] text-muted-foreground">Org + FY2026</span>
          </div>
        </Card>

        <Card className="p-4 bg-card/60 backdrop-blur-sm border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Active Series Rules</span>
            <Sparkles className="h-4 w-4 text-emerald-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-xl font-bold text-foreground">{seriesList.filter((s) => s.isActive).length}</span>
            <span className="text-[10px] text-muted-foreground">rules live</span>
          </div>
        </Card>

        <Card className="p-4 bg-card/60 backdrop-blur-sm border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Superadmin Fixed</span>
            <ShieldCheck className="h-4 w-4 text-purple-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-sm font-mono font-bold text-purple-600 dark:text-purple-400">DT-26-SA01</span>
            <span className="text-[10px] text-muted-foreground">system role</span>
          </div>
        </Card>

        <Card className="p-4 bg-card/60 backdrop-blur-sm border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Sequence Padding</span>
            <CheckCircle2 className="h-4 w-4 text-blue-500" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-xl font-bold text-foreground">3 Digits</span>
            <span className="text-[10px] text-muted-foreground">e.g. 001, 002...</span>
          </div>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card className="border-border overflow-hidden">
        <CardHeader className="p-4 pb-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-border/60 bg-muted/20">
          <div>
            <CardTitle className="text-sm font-semibold">Configured Department & Role Codes</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Rules are evaluated sequentially. Specific role keyword criteria take precedence over general department defaults.
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search department, role, code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-xs bg-background"
            />
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-12 flex justify-center items-center">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="p-8 text-center text-sm text-destructive">
              Failed to load employee code series.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/40 border-b border-border/80 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="py-2.5 px-4">Department</th>
                    <th className="py-2.5 px-4">Role Text Criteria</th>
                    <th className="py-2.5 px-4">Code Prefix</th>
                    <th className="py-2.5 px-4">Generated Example</th>
                    <th className="py-2.5 px-4 text-center">In Use</th>
                    <th className="py-2.5 px-4 text-center">Status</th>
                    <th className="py-2.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-muted-foreground">
                        No series rules match your search.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((row) => {
                      const isAny = !row.roleKeywords || row.roleKeywords.length === 0 || row.roleKeywords.includes('any');
                      return (
                        <tr key={row.id} className="hover:bg-muted/30 transition-colors">
                          <td className="py-3 px-4 font-medium text-foreground capitalize">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{row.department}</span>
                              {row.label && (
                                <span className="text-[11px] text-muted-foreground">({row.label})</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {isAny ? (
                              <Badge variant="secondary" className="text-[10px] font-medium bg-muted text-muted-foreground">
                                any role in department
                              </Badge>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {row.roleKeywords.map((kw) => (
                                  <Badge
                                    key={kw}
                                    variant="outline"
                                    className="text-[10px] font-mono bg-sky-50 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300 border-sky-200 dark:border-sky-800"
                                  >
                                    contains &quot;{kw}&quot;
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-mono font-bold text-sm text-primary bg-primary/10 px-2 py-0.5 rounded">
                              {row.code}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-mono font-medium text-xs text-foreground bg-muted/60 px-2.5 py-1 rounded-md border border-border/80">
                              {row.example || `DT-26-${row.code}001`}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`text-xs font-semibold ${row.inUse ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                              {row.inUse ?? 0}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge
                              variant="outline"
                              className={`text-[10px] font-medium ${
                                row.isActive
                                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                  : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {row.isActive ? 'Active' : 'Disabled'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEdit(row)}
                                className="h-7 px-2 text-xs gap-1"
                              >
                                <Pencil className="h-3 w-3" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(row)}
                                disabled={(row.inUse ?? 0) > 0}
                                className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-30"
                                title={(row.inUse ?? 0) > 0 ? 'Cannot delete rule while codes are in use' : 'Delete rule'}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}

                  {/* Built-in System Rules Row: Superadmin */}
                  <tr className="bg-purple-50/30 dark:bg-purple-950/20 border-t-2 border-border/80">
                    <td className="py-3 px-4 font-semibold text-purple-900 dark:text-purple-300">
                      <div className="flex items-center gap-1.5">
                        <ShieldCheck className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                        <span>Superadmin</span>
                        <span className="text-[10px] text-muted-foreground font-normal">(Dept or role === SUPERADMIN)</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground italic text-[11px]">—</td>
                    <td className="py-3 px-4">
                      <span className="font-mono font-bold text-sm text-purple-700 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/40 px-2 py-0.5 rounded">
                        SA
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-mono font-bold text-xs text-purple-700 dark:text-purple-300 bg-purple-100/70 dark:bg-purple-950/60 px-2.5 py-1 rounded-md border border-purple-200 dark:border-purple-800">
                        DT-26-SA01
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-muted-foreground text-[11px]">Singleton</td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant="outline" className="text-[10px] font-semibold bg-purple-100/80 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200 dark:border-purple-800">
                        System Fixed
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right text-muted-foreground text-[11px] pr-5">
                      Built-in rule
                    </td>
                  </tr>

                  {/* Built-in System Rules Row: Blank / Unknown */}
                  <tr className="bg-muted/15">
                    <td className="py-3 px-4 font-medium text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                        <span>Blank / Unknown Dept</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground italic text-[11px]">—</td>
                    <td className="py-3 px-4 text-muted-foreground font-mono text-[11px]">—</td>
                    <td className="py-3 px-4">
                      <span className="font-mono text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-md border border-border">
                        no code
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-muted-foreground text-[11px]">—</td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant="outline" className="text-[10px] font-semibold bg-muted text-muted-foreground">
                        Default Fallback
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right text-muted-foreground text-[11px] pr-5">
                      No code issued
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Code Generation Rules & Specs Guide */}
      <Card className="border-border bg-card/60 backdrop-blur-sm">
        <CardHeader className="p-4 pb-2">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-primary" />
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              How Employee Code Generation Works
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-1 space-y-2 text-xs text-muted-foreground leading-relaxed">
          <p>
            • <strong>Automatic Sequence Issuance:</strong> When an employee joins or is converted from onboarding, the system finds the highest sequence in that series (e.g. <span className="font-mono">DT-26-FD007</span>) and issues the next integer (<span className="font-mono">DT-26-FD008</span>).
          </p>
          <p>
            • <strong>Department Transfers & Code Retirement:</strong> When an employee switches department (e.g. from Development to HR), their new series code is automatically generated. Their previous code is safely stored in <span className="font-mono">previousEmployeeCodes</span> and is permanently retired to ensure no code is ever duplicated.
          </p>
          <p>
            • <strong>Role Text Matching:</strong> For departments with multiple streams (such as Sales and Creative), any role designation containing keywords like <span className="font-mono">BDE</span> or <span className="font-mono">video</span> will match the specialized series before falling back to the department&apos;s general code.
          </p>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              {form.id ? 'Edit Employee Code Series' : 'Add Employee Code Series'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="department" className="text-xs font-semibold">
                Department Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="department"
                placeholder="e.g. Development, Sales, HR, Creative"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                required
                className="text-xs"
              />
              <p className="text-[11px] text-muted-foreground">Department slug to which this rule applies.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="code" className="text-xs font-semibold">
                Two-Letter Series Code <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="code"
                  placeholder="e.g. FD, HR, BD, SL"
                  maxLength={2}
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  required
                  className="font-mono uppercase text-sm tracking-widest font-bold w-28"
                />
                {/^[A-Z]{2}$/.test(form.code.trim()) && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono bg-muted/60 px-3 py-1.5 rounded border border-border">
                    <span>Preview:</span>
                    <strong className="text-primary font-bold">DT-26-{form.code.trim().toUpperCase()}001</strong>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="label" className="text-xs font-semibold">
                Label / Description (Optional)
              </Label>
              <Input
                id="label"
                placeholder="e.g. Development (Frontend / Full-stack)"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="text-xs"
              />
            </div>

            {(() => {
              const cleanDept = form.department.trim().toLowerCase();
              const existingCatchAll = seriesList.find(
                (s) =>
                  s.department.toLowerCase() === cleanDept &&
                  (!s.roleKeywords || s.roleKeywords.length === 0 || s.roleKeywords.includes('any')) &&
                  s.id !== form.id
              );
              if (!existingCatchAll || !form.isAnyRole) return null;
              return (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-xs flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-semibold">
                      Department &quot;{form.department}&quot; already has a general fallback ({existingCatchAll.code})
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      If this code is for a specific role (like {form.label || 'Graphic Designer'}), turn off &quot;Apply to all roles&quot; and enter keywords below.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          isAnyRole: false,
                          roleKeywordsText: prev.roleKeywordsText || prev.label.toLowerCase() || '',
                        }));
                      }}
                      className="text-xs font-semibold text-primary hover:underline cursor-pointer block pt-0.5"
                    >
                      Switch to specific role matching &rarr;
                    </button>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-2 pt-1 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-semibold">Apply to all roles in department?</Label>
                  <p className="text-[11px] text-muted-foreground">
                    If enabled, any employee in this department matches this code.
                  </p>
                </div>
                <Switch
                  checked={form.isAnyRole}
                  onCheckedChange={(checked) => setForm({ ...form, isAnyRole: checked })}
                />
              </div>

              {!form.isAnyRole && (
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="keywords" className="text-xs font-semibold">
                      Specific Role Keywords (Comma separated) <span className="text-destructive">*</span>
                    </Label>
                    {form.label && !form.roleKeywordsText && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, roleKeywordsText: form.label.toLowerCase() })}
                        className="text-[11px] text-primary hover:underline"
                      >
                        Use &quot;{form.label}&quot;
                      </button>
                    )}
                  </div>
                  <Input
                    id="keywords"
                    placeholder="e.g. bde, bd, business development"
                    value={form.roleKeywordsText}
                    onChange={(e) => setForm({ ...form, roleKeywordsText: e.target.value })}
                    required={!form.isAnyRole}
                    className="text-xs"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Matches if the designation contains any of these keywords (case-insensitive).
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div>
                <Label className="text-xs font-semibold">Active Series Rule</Label>
                <p className="text-[11px] text-muted-foreground">Disable to pause minting this series.</p>
              </div>
              <Switch
                checked={form.isActive}
                onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
              />
            </div>

            <DialogFooter className="gap-2 pt-3">
              <Button type="button" variant="outline" size="sm" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={createSeries.isPending || updateSeries.isPending}>
                {form.id ? 'Save Changes' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default HrEmployeeCodesPage;
